import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FormFields,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceAdministratorServiceTypeEdit() {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-service-types/${pageId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-service-types/${pageId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);

  const preProcessRequestData = data => {
    const { status, createdByUserId, lastModifiedByUserId, id, ...other } =
      data;
    return {
      status: status === 'true',
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { ...other } = data;
    return {
      ...other,
    };
  };

  const submitEditForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEADMINISTRATORSERVICETYPE_UPDATE_SUCCESS',
          defaultMessage:
            'Service Administrator Service Type was saved successfully',
        }),
      );
      setInitialValues(preProcessFetchData(response?.data));
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEADMINISTRATORSERVICETYPE_UPDATE_FAILURE',
          defaultMessage:
            'Error: Could not save the service administrator service type',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    setFormErrors(null);
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(response?.data),
      });
      setLoading(false);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEADMINISTRATORSERVICETYPE_DATA_FETCH_FAILURE',
          defaultMessage:
            'Error: Could not fetch the service administrator service type data',
        }),
      });
    }
  };

  useEffect(() => {
    fetchInitialData(pageId);
  }, [pageId]);

  const formProps = {
    customComponents: FormFields,
    schema: editSchema,
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEADMINISTRATORSERVICETYPES_BREADCRUMB',
        defaultMessage: 'Service Administrator Service Types',
      }),
      icon: IconLibrary.ServiceAdministratorServiceTypes,
      onClick: () => {
        navigate('/serviceadministratorservicetypes');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_SERVICEADMINISTRATORSERVICETYPE_EDIT_BREADCRUMB',
        defaultMessage: 'Edit Service Administrator Service Type',
      }),
      icon: IconLibrary.ServiceAdministratorServiceType,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_SERVICEADMINISTRATORSERVICETYPES_STACKABLE_BUTTON',
        defaultMessage: 'Create Service Administrator Service Type',
      }),
      Icon: IconLibrary.ServiceAdministratorServiceType,
      variant: 'outlined',
      onClick: () => navigate('/serviceadministratorservicetypes/create'),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES',
    },
    {
      label: formatMessage({
        id: 'EDIT_SERVICEADMINISTRATORSERVICETYPES_STACKABLE_BUTTON',
        defaultMessage: 'List Service Administrator Service Types',
      }),
      Icon: IconLibrary.ServiceAdministratorServiceTypes,
      variant: 'outlined',
      onClick: () => navigate('/serviceadministratorservicetypes'),
    },
  ];

  return (
    <Layout>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing..." />
        </LoadingEvent>
      )}

      {!loading && (
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
      )}

      {!loading && (
        <PageSection
          label={formatMessage({
            id: 'SERVICEADMINISTRATORSERVICETYPE_EDIT_HEADING',
            defaultMessage: 'Edit Service Administrator Service Type',
          })}>
          <Box>
            <CRUDComponent
              errors={formErrors}
              type={CRUD_COMPONENT_TYPE.FORM}
              formProps={formProps}>
              <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
                <PermissionButton
                  permissionName="CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES"
                  variant="contained"
                  startIcon={<IconLibrary.Save />}
                  color="primary"
                  onClick={() => editFormRef.current?.submit()}>
                  {formatMessage({
                    id: 'SERVICEADMINISTRATORSERVICETYPE_UPDATE_SAVE_BUTTON',
                    defaultMessage: 'Save Service Administrator Service Type',
                  })}
                </PermissionButton>
                <PermissionButton
                  permissionName="CAN_CREATE_EDIT_MAINTENANCE_DETAIL_PAGES"
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => editFormRef.current?.reset()}>
                  {formatMessage({
                    id: 'SERVICEADMINISTRATORSERVICETYPE_UPDATE_RESET_BUTTON',
                    defaultMessage: 'Reset',
                  })}
                </PermissionButton>
              </Box>
            </CRUDComponent>
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(ServiceAdministratorServiceTypeEdit);
